import type React from 'react';

import { HEX_COLOR, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';
/**
 * @param color
 * @param opacity (0.0 - 1.0)
 *
 * Returns `undefined` rather than `null` for a missing colour: nearly every caller drops the
 * result straight into a `style={{ color: … }}` / `backgroundColor` slot, and csstype's `Color`
 * accepts `undefined` but not `null`. React omits the declaration for either value, so the
 * rendered output is unchanged.
 */
export function withOpacity(color?: string, opacity?: number): HEX_COLOR | undefined {
    if (!color || opacity === undefined) return undefined;

    const hexWithoutHash = color.replace('#', '');

    const alpha = Math.round(Math.min(Math.max(opacity, 0), 1) * 255)
        .toString(16)
        .padStart(2, '0');

    const expandedHex =
        hexWithoutHash.length === 3
            ? hexWithoutHash
                  .split('')
                  .map((char) => char + char)
                  .join('')
            : hexWithoutHash.slice(0, 6);

    return `#${expandedHex}${alpha}` as HEX_COLOR;
}

type RgbColor = {
    r: number;
    g: number;
    b: number;
    a?: number;
};

/**
 * Checks if a given string is a valid hex color in the formats #RGB, #RGBA, #RRGGBB, or #RRGGBBAA.
 *
 * A valid hex color can have:
 * - 3 characters (#RGB)
 * - 4 characters (#RGBA)
 * - 6 characters (#RRGGBB)
 * - 8 characters (#RRGGBBAA)
 *
 * @param {string} hex - The hex color string to validate.
 * @returns {boolean} `true` if the input is a valid hex color, otherwise `false`.
 */
export function isValidHexColor(hex: string): boolean {
    return /^#([a-fA-F0-9]{3,4}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/.test(hex);
}

/**
 * Converts a hex color code to an RGB(A) object.
 * Supports both 6-character (#RRGGBB) and 8-character (#RRGGBBAA) formats.
 *
 * @param hex - The hex color code (e.g., "#ffffff" or "#ffffffff").
 * @returns An object containing `r`, `g`, `b`, and optionally `a` (alpha).
 * @throws Will throw an error if the hex color is invalid.
 */
export function hexToRgb(hex: string): RgbColor {
    if (!isValidHexColor(hex)) {
        throw new Error(`Invalid hex color format. Use #RGB, #RGBA, #RRGGBB, or #RRGGBBAA. hex: ${hex}`);
    }

    let r: number, g: number, b: number, a: number | undefined;

    if (hex.length === 4) {
        // #RGB
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 5) {
        // #RGBA
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
        a = parseInt(hex[4] + hex[4], 16) / 255;
    } else if (hex.length === 7) {
        // #RRGGBB
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
    } else if (hex.length === 9) {
        // #RRGGBBAA
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
        a = parseInt(hex.slice(7, 9), 16) / 255;
    } else {
        throw new Error(`Unexpected hex format: ${hex}`);
    }

    return { r, g, b, a };
}

export const hexToRgbString = (hex: string, separator: string = ', '): string => {
    const rgb = hexToRgb(hex);
    return [rgb.r, rgb.g, rgb.b].join(separator);
};

/**
 * Adds rgb versions of the colors to the css variables.
 * This can be used later in tailwind to specify opacity for the colors in class, like
 * bg-[rgba(var(--primary-color-rgb),0.5)]
 * @param style - The object to add the rgb versions to.
 * @returns The object with the rgb versions added.
 * @example
 * const style = {
 *     '--primary-color': '#000000',
 *     '--on-primary-color': '#ffffff',
 * };
 * const styleWithRgb = addRgbVersions(style);
 * console.log(styleWithRgb);
 * // {
 * //     '--primary-color': '#000000',
 * //     '--on-primary-color': '#ffffff',
 * //     '--primary-color-rgb': '0, 0, 0',
 * //     '--on-primary-color-rgb': '255, 255, 255',
 * // }
 *
 */
export function addRgbVersions(style: Record<string, string>): React.CSSProperties {
    return Object.entries(style).reduce((result, [key, value]) => {
        result[`${key}-rgb`] = value ? hexToRgbString(value) : '0, 0, 0';
        return result;
    }, style) as React.CSSProperties;
}

/**
 * Calculates the relative luminance of an RGB color.
 * The math for this function was provided by ChatGPT and follows the W3C standard
 * for relative luminance (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance).
 *
 * **WARNING**: This math is complex and not recommended to modify unless you're
 * certain about the impact. Any change could break theme detection.
 *
 * @param rgb - The RGB color object.
 * @returns The relative luminance value (0 to 1).
 */
export function getRelativeLuminance(rgb: RgbColor): number {
    // Normalize RGB values to [0, 1]
    const [R, G, B] = [rgb.r, rgb.g, rgb.b].map((v) => v / 255);

    // Apply gamma correction
    const [rCorrected, gCorrected, bCorrected] = [R, G, B].map((c) =>
        c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );

    // Calculate relative luminance
    return 0.2126 * rCorrected + 0.7152 * gCorrected + 0.0722 * bCorrected;
}

export function isThemeLight(backgroundColorHex: string, textColorHex: string) {
    const bgRgb = hexToRgb(backgroundColorHex);
    const textRgb = hexToRgb(textColorHex);

    const bgLuminance = getRelativeLuminance(bgRgb);
    const textLuminance = getRelativeLuminance(textRgb);

    // If the background is lighter than the text, the theme is light
    return bgLuminance > textLuminance;
}

/**
 * Convert an {r,g,b,a} object into HSBA.
 * @param {number} r 0–255
 * @param {number} g 0–255
 * @param {number} b 0–255
 * @param {number} a 0–1
 * @returns {{ h: number, s: number, b: number, a: number }}
 *   h in [0,360), s and b in [0,100], a unchanged
 */
function rgbaToHsba({ r, g, b, a }: RgbColor): { h: number; s: number; b: number; a?: number } {
    // normalize to [0,1]
    const nr = r / 255;
    const ng = g / 255;
    const nb = b / 255;

    const max = Math.max(nr, ng, nb);
    const min = Math.min(nr, ng, nb);
    const delta = max - min;

    // hue calculation
    let h;
    if (delta === 0) {
        h = 0;
    } else if (max === nr) {
        h = 60 * (((ng - nb) / delta) % 6);
    } else if (max === ng) {
        h = 60 * ((nb - nr) / delta + 2);
    } else {
        h = 60 * ((nr - ng) / delta + 4);
    }
    if (h < 0) h += 360;

    // saturation and brightness/value
    const s = max === 0 ? 0 : (delta / max) * 100;
    const brightness = max * 100;

    return {
        h: Math.round(h),
        s: Math.round(s),
        b: Math.round(brightness),
        a: a
    };
}

/**
 * Determines if the background color is bright enough.
 * @param backgroundColorHex - The hex color code of the background.
 * @param expectedBrightness - The expected brightness value.
 * @returns `true` if the background color is bright enough, `false` otherwise.
 */
export function isBackgroundBrightEnough(backgroundColorHex: string, expectedBrightness: number): boolean {
    const bgRgb = hexToRgb(backgroundColorHex);
    const bgBrightness = rgbaToHsba(bgRgb);
    return bgBrightness.b > expectedBrightness;
}

/**
 * Blends a foreground color with a background color. This is useful to determine non-transparent foreground color when used on a background color.
 * @param fg - The foreground color.
 * @param bg - The background color.
 * @returns The blended color.
 */
export function blendColors(fg: RgbColor, bg: RgbColor) {
    const alpha = fg.a ?? 1;

    // Apply the blending formula to each channel
    const r = Math.round(fg.r * alpha + bg.r * (1 - alpha));
    const g = Math.round(fg.g * alpha + bg.g * (1 - alpha));
    const b = Math.round(fg.b * alpha + bg.b * (1 - alpha));

    return { r, g, b };
}

export function rgbToHex(rgb: RgbColor): HEX_COLOR {
    return `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}${rgb.a ? rgb.a.toString(16).padStart(2, '0') : ''}`;
}

export type ContrastAccessibility = {
    ratio: number;
    basic: boolean;
    AA: boolean;
    AAA: boolean;
};

export function checkContrast(foregroundColor: RgbColor, backgroundColor: RgbColor): ContrastAccessibility {
    const foregroundLuminance = getRelativeLuminance(foregroundColor);
    const backgroundLuminance = getRelativeLuminance(backgroundColor);
    const brighterColor = Math.max(backgroundLuminance, foregroundLuminance);
    const darkerColor = Math.min(backgroundLuminance, foregroundLuminance);
    const ratio = Math.round(((brighterColor + 0.05) / (darkerColor + 0.05)) * 10) / 10;
    return { ratio, basic: ratio >= 3.0, AA: ratio >= 4.5, AAA: ratio >= 7 };
}

// Takes a plain `WebsiteStyle`: only the named colour slots below are read, and the former
// `& Record<string, string>` intersection made every real `WebsiteStyle` unassignable because its
// optional slots are `HEX_COLOR | undefined`.
export function getPageColorVariables(websiteColors: WebsiteStyle): React.CSSProperties {
    return addRgbVersions({
        '--primary-color': websiteColors.primaryColor,
        '--on-primary-color': websiteColors.onPrimaryColor,
        '--background-color': websiteColors.backgroundColor,
        '--on-background-color': websiteColors.onBackgroundColor,
        '--surface-color': websiteColors.surfaceColor,
        '--on-surface-color': websiteColors.onSurfaceColor,
        '--secondary-color': websiteColors.secondaryColor,
        '--non-transparent-surface-color':
            websiteColors.nonTransparentSurfaceColor ||
            rgbToHex(blendColors(hexToRgb(websiteColors.surfaceColor), hexToRgb(websiteColors.backgroundColor))),
        '--on-non-transparent-surface-color':
            websiteColors.onNonTransparentSurfaceColor || websiteColors.onSurfaceColor,
        '--surface-tint-color':
            websiteColors.surfaceTintColor ??
            withOpacity(websiteColors.primaryColor, 0.2) ??
            websiteColors.primaryColor,
        '--primary-middle-color': websiteColors.primaryMiddleColor ?? websiteColors.primaryColor,
        '--primary-dark-color': websiteColors.primaryDarkColor ?? websiteColors.primaryColor
    });
}

export function getCssVar(element: HTMLElement, name: `--${string}`): string {
    return getComputedStyle(element).getPropertyValue(name).trim();
}
